export const REVIEW_AND_SUBMIT_NODE_TYPE = "review_and_submit";
export const REVIEW_AND_SUBMIT_DEFAULT_PROMPT =
  "Please review your request.";
export const REVIEW_AND_SUBMIT_MAXIMUM_PROMPT_LENGTH = 1024;
export const REVIEW_AND_SUBMIT_MAXIMUM_LABEL_LENGTH = 1024;
export const REVIEW_AND_SUBMIT_MAXIMUM_BINDINGS = 25;
export const REVIEW_AND_SUBMIT_PATHS = Object.freeze([
  { id: "submitted", label: "Submitted" },
  { id: "change_requested", label: "Change Requested" },
  { id: "cancelled", label: "Cancelled" },
]);

const SUPPORTED_INPUT_TYPES = new Set([
  "text",
  "integer",
  "decimal",
  "email",
  "phone",
  "date",
]);

const normalizeNodeType = value =>
  String(value || "").trim().toLowerCase();

export const normalizeReviewFieldKey = value =>
  String(value || "").trim().toLowerCase();

const edgeSource = edge => edge?.sourceNodeId || edge?.source || "";
const edgeTarget = edge => edge?.targetNodeId || edge?.target || "";

const buildAdjacency = edges => {
  const adjacency = new Map();
  (Array.isArray(edges) ? edges : []).forEach(edge => {
    const source = String(edgeSource(edge) || "");
    const target = String(edgeTarget(edge) || "");
    if (!source || !target) return;
    if (!adjacency.has(source)) adjacency.set(source, []);
    adjacency.get(source).push(target);
  });
  return adjacency;
};

export const canReachReviewNode = (
  sourceNodeId,
  reviewNodeId,
  edges
) => {
  const source = String(sourceNodeId || "");
  const target = String(reviewNodeId || "");
  if (!source || !target || source === target) return false;

  const adjacency = buildAdjacency(edges);
  const pending = [source];
  const visited = new Set([source]);
  while (pending.length) {
    const current = pending.shift();
    for (const next of adjacency.get(current) || []) {
      if (next === target) return true;
      if (!visited.has(next)) {
        visited.add(next);
        pending.push(next);
      }
    }
  }
  return false;
};

const canReachReviewFromHandle = (sourceNodeId, sourceHandle, reviewNodeId, edges) =>
  (Array.isArray(edges) ? edges : [])
    .filter(edge =>
      String(edgeSource(edge)) === String(sourceNodeId) &&
      String(edge?.sourceHandle || "").trim().toLowerCase() === sourceHandle)
    .some(edge =>
      String(edgeTarget(edge)) === String(reviewNodeId) ||
      canReachReviewNode(edgeTarget(edge), reviewNodeId, edges));

export const createReviewAndSubmitConfig = () => ({
  promptText: REVIEW_AND_SUBMIT_DEFAULT_PROMPT,
  detailBindings: [],
});

export const getReviewDecisionTablePriceOutputs = (nodes, edges, reviewNodeId) =>
  (Array.isArray(nodes) ? nodes : [])
    .filter(node =>
      normalizeNodeType(node?.type) === "decision_table" &&
      canReachReviewNode(node?.id, reviewNodeId, edges)
    )
    .flatMap(node => (Array.isArray(node?.data?.config?.outputs)
      ? node.data.config.outputs
      : []).filter(output => normalizeNodeType(output?.dataType) === "decimal")
      .map(output => ({
        nodeId: String(node.id),
        fieldKey: normalizeReviewFieldKey(output.fieldKey),
        label: String(output.label || output.key || output.fieldKey || "Decimal output"),
      })));

export const getReviewInputCaptureFields = (
  nodes,
  edges,
  reviewNodeId
) =>
  (Array.isArray(nodes) ? nodes : [])
    .filter(node => normalizeNodeType(node?.type) === "input_capture")
    .map(node => {
      const config = node?.data?.config || {};
      const inputType = normalizeNodeType(config.inputType || "text");
      return {
        sourceInputCaptureNodeId: String(node?.id || ""),
        fieldKey: normalizeReviewFieldKey(config.fieldKey),
        inputType,
        question: String(config.question || "").trim(),
        label: String(
          config.question || node?.data?.label || "Input Capture"
        ).trim(),
        isSupported: SUPPORTED_INPUT_TYPES.has(inputType),
        isUpstream: canReachReviewNode(node?.id, reviewNodeId, edges),
      };
    })
    .filter(field =>
      field.sourceInputCaptureNodeId &&
      field.fieldKey &&
      field.isSupported &&
      field.isUpstream
    );

export const normalizeReviewAndSubmitConfig = config => ({
  promptText:
    String(config?.promptText ?? "").trim() ||
    REVIEW_AND_SUBMIT_DEFAULT_PROMPT,
  detailBindings: (Array.isArray(config?.detailBindings)
    ? config.detailBindings
    : []
  ).map((binding, index) => ({
    sourceInputCaptureNodeId: String(
      binding?.sourceInputCaptureNodeId || ""
    ).trim(),
    fieldKey: normalizeReviewFieldKey(binding?.fieldKey),
    labelOverride:
      String(binding?.labelOverride || "").trim() || null,
    sortOrder: index,
  })),
  ...(config?.servicePricing?.source === "DecisionTable"
    ? { servicePricing: {
      source: "DecisionTable",
      decisionTableNodeId: String(config.servicePricing.decisionTableNodeId || ""),
      fieldKey: normalizeReviewFieldKey(config.servicePricing.fieldKey),
    } }
    : {}),
});

export const validateReviewAndSubmitConfig = (
  config,
  nodes,
  edges,
  reviewNodeId,
  { requireCommerceSource = true } = {}
) => {
  const rawPrompt = String(config?.promptText ?? "").trim();
  if (!rawPrompt) {
    return {
      isValid: false,
      reason: "prompt_required",
      message: "Enter a review message.",
    };
  }
  if (rawPrompt.length > REVIEW_AND_SUBMIT_MAXIMUM_PROMPT_LENGTH) {
    return {
      isValid: false,
      reason: "prompt_too_long",
      message: `Review message cannot exceed ${REVIEW_AND_SUBMIT_MAXIMUM_PROMPT_LENGTH} characters.`,
    };
  }

  const bindings = Array.isArray(config?.detailBindings)
    ? config.detailBindings
    : [];
  if (bindings.length > REVIEW_AND_SUBMIT_MAXIMUM_BINDINGS) {
    return {
      isValid: false,
      reason: "too_many_bindings",
      message: `Choose no more than ${REVIEW_AND_SUBMIT_MAXIMUM_BINDINGS} captured fields.`,
    };
  }

  const nodeById = new Map(
    (Array.isArray(nodes) ? nodes : []).map(node => [
      String(node?.id || ""),
      node,
    ])
  );
  const seenKeys = new Set();
  for (const binding of bindings) {
    const sourceId = String(
      binding?.sourceInputCaptureNodeId || ""
    ).trim();
    const fieldKey = normalizeReviewFieldKey(binding?.fieldKey);
    const labelOverride = String(binding?.labelOverride || "").trim();
    const sourceNode = nodeById.get(sourceId);
    const sourceConfig = sourceNode?.data?.config || {};
    const currentKey = normalizeReviewFieldKey(sourceConfig.fieldKey);
    const inputType = normalizeNodeType(sourceConfig.inputType || "text");

    if (!sourceNode || normalizeNodeType(sourceNode.type) !== "input_capture") {
      return {
        isValid: false,
        reason: "missing_source",
        message: "Remove or replace a detail whose Input Capture node is missing.",
      };
    }
    if (!SUPPORTED_INPUT_TYPES.has(inputType)) {
      return {
        isValid: false,
        reason: "unsupported_type",
        message: "A selected captured field has an unsupported input type.",
      };
    }
    if (!fieldKey || fieldKey !== currentKey) {
      return {
        isValid: false,
        reason: "stale_field_key",
        message: "A selected captured field key changed. Remove and add it again.",
      };
    }
    if (!canReachReviewNode(sourceId, reviewNodeId, edges)) {
      return {
        isValid: false,
        reason: "source_not_upstream",
        message: "Each selected field must be captured earlier in this flow.",
      };
    }
    if (seenKeys.has(fieldKey)) {
      return {
        isValid: false,
        reason: "duplicate_field_key",
        message: "Each captured field can be included only once.",
      };
    }
    seenKeys.add(fieldKey);
    if (labelOverride.length > REVIEW_AND_SUBMIT_MAXIMUM_LABEL_LENGTH) {
      return {
        isValid: false,
        reason: "label_too_long",
        message: `Display labels cannot exceed ${REVIEW_AND_SUBMIT_MAXIMUM_LABEL_LENGTH} characters.`,
      };
    }
  }

  if (requireCommerceSource) {
    const hasCommerceSource = (Array.isArray(nodes) ? nodes : []).some(node => {
      const type = normalizeNodeType(node?.type);
      if (type === "prepare_commerce_service") {
        return canReachReviewFromHandle(node.id, "prepared", reviewNodeId, edges);
      }
      return type === "present_catalogue_results" &&
        !!node?.data?.config?.commerce &&
        canReachReviewFromHandle(node.id, "selected", reviewNodeId, edges);
    });
    if (!hasCommerceSource) {
      return {
        isValid: false,
        reason: "commerce_source_required",
        message:
          "Review & Submit requires an earlier successful Commerce producer.",
      };
    }
  }


  if (config?.servicePricing?.source === "DecisionTable") {
    const sourceId = String(config.servicePricing.decisionTableNodeId || "");
    const fieldKey = normalizeReviewFieldKey(config.servicePricing.fieldKey);
    const compatible = getReviewDecisionTablePriceOutputs(
      nodes, edges, reviewNodeId
    ).some(output => output.nodeId === sourceId && output.fieldKey === fieldKey);
    if (!compatible) {
      return {
        isValid: false,
        reason: "dynamic_price_source_invalid",
        message: "Select an upstream Decimal Decision Table output for service pricing.",
      };
    }
  }

  return {
    isValid: true,
    reason: "",
    message: "",
    normalized: normalizeReviewAndSubmitConfig(config),
  };
};
